import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Truck, Mail } from "lucide-react";

import { fetchOrder } from "../../lib/api";
import { useTemplate } from "../../context/TemplateContext";

/**
 * SRC-styled order confirmation. Centered card with:
 *   - Success glyph + big "Thanks — order confirmed."
 *   - Order number + email
 *   - Shipping address summary
 *   - Line items summary
 *   - Support / continue shopping CTA
 */
export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const { basePath, slug } = useTemplate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder(orderNumber).then(setOrder).finally(() => setLoading(false));
  }, [orderNumber, slug]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center animate-pulse">
        <div className="w-14 h-14 rounded-full bg-neutral-100 mx-auto" />
        <div className="h-10 bg-neutral-100 w-2/3 mx-auto mt-6" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="order-not-found">
        <h1 className="font-display text-4xl">Order not found.</h1>
        <p className="text-neutral-500 mt-2">We couldn&apos;t find order #{orderNumber}.</p>
        <Link to={`${basePath}/shop`} className="inline-block mt-8 bg-black text-white px-8 py-4 text-xs uppercase tracking-widest">
          Back to shop
        </Link>
      </div>
    );
  }

  const items = order.items || [];
  const email = order.email || "";
  const ship = order.shipping_address || order;

  return (
    <div data-testid="order-success-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
      <div className="text-center">
        <div className="w-14 h-14 rounded-full bg-black text-white grid place-items-center mx-auto">
          <Check className="w-6 h-6" strokeWidth={2.5} />
        </div>
        <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mt-6">— Order confirmed</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-none">Thanks. You&apos;re all set.</h1>
        <p className="text-sm text-neutral-500 mt-4">
          Order <span data-testid="order-number" className="text-black font-semibold">#{order.order_number || orderNumber}</span>
          {email && <> · confirmation sent to <span className="text-black">{email}</span></>}
        </p>
      </div>

      <div className="mt-12 border border-neutral-200 divide-y divide-neutral-200">
        <div className="p-6 grid sm:grid-cols-2 gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Shipping to</p>
            <p className="font-heading text-sm">{ship.full_name || ship.name}</p>
            {ship.address_line1 && <p className="text-sm text-neutral-600">{ship.address_line1}</p>}
            {ship.address_line2 && <p className="text-sm text-neutral-600">{ship.address_line2}</p>}
            <p className="text-sm text-neutral-600">
              {[ship.city, ship.state, ship.postal_code].filter(Boolean).join(", ")}
            </p>
            {ship.country && <p className="text-sm text-neutral-600">{ship.country}</p>}
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Delivery</p>
            <div className="flex items-start gap-2 text-sm">
              <Truck className="w-4 h-4 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-heading">Standard shipping</p>
                <p className="text-neutral-500 text-xs mt-1">Arrives in 3–5 business days.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Items</p>
          <ul className="space-y-3">
            {items.map((it, i) => (
              <li key={i} data-testid={`order-line-${i}`} className="flex gap-4">
                {it.image && <img src={it.image} alt={it.name} className="w-16 h-20 object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm">{it.name}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {[it.size || it.variant?.size, it.color || it.variant?.color].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-xs text-neutral-500">Qty {it.quantity || it.qty}</p>
                </div>
                <p className="font-heading text-sm">${(Number(it.price || 0) * Number(it.quantity || it.qty || 1)).toFixed(2)}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-6 grid sm:grid-cols-2 gap-6">
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>${Number(order.subtotal || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-neutral-500"><span>Shipping</span><span>{Number(order.shipping || 0) === 0 ? "Free" : `$${Number(order.shipping || 0).toFixed(2)}`}</span></div>
            <div className="flex justify-between font-heading text-base pt-2 border-t border-neutral-200 mt-2"><span>Total</span><span data-testid="order-total">${Number(order.total || 0).toFixed(2)}</span></div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Need help?</p>
            <a href="mailto:hello@example.com" className="inline-flex items-center gap-2 text-sm link-underline pb-0.5">
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              Contact support
            </a>
          </div>
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link to={`${basePath}/shop`} className="inline-block bg-black text-white hover:bg-neutral-800 px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors">
          Continue shopping
        </Link>
      </div>
    </div>
  );
}
